import { logger } from "@formant/core/common/logger";
import { Timestamp } from "@formant/core/model/Timestamp";

import { IHasPeerConnection } from "./IHasPeerConnection";
import { IHasRemoteDeviceId } from "./IHasRemoteDeviceId";
import { IRtcSessionMetricsMessageCounts } from "./models/IRtcSessionMetricsMessageCounts";
import { IRtcStreamMessage } from "./models/IRtcStreamMessage";
import { IRtcStreamsInfo } from "./models/IRtcStreamsInfo";
import { encodeStreamId } from "./utils/encodeStreamId";

type IRtcConnection = IHasPeerConnection & IHasRemoteDeviceId;

export class RtcSessionMetrics {
    public counts: IRtcSessionMetricsMessageCounts = {
        localSent: new Map<string, number>(),
        localReceived: new Map<string, number>(),
        remoteSent: new Map<string, number>(),
        remoteReceived: new Map<string, number>(),
    };

    private readonly sessionId: string;
    private readonly connection: IRtcConnection;

    constructor(
        sessionId: string,
        connection: IHasPeerConnection & IHasRemoteDeviceId,
    ) {
        this.sessionId = sessionId;
        this.connection = connection;
    }

    public updateRemoteMessagesCounts(message: IRtcStreamMessage) {
        if (!message.payload.streamsInfo) {
            return;
        }

        this.counts.remoteSent = new Map<string, number>();
        this.counts.remoteReceived = new Map<string, number>();
        for (const item of message.payload.streamsInfo.items) {
            this.counts.remoteSent.set(item.streamId, item.sentCount);
            this.counts.remoteReceived.set(item.streamId, item.receivedCount);
        }
    }

    public getLocalStreamsInfo(timestamp: Timestamp): IRtcStreamsInfo {
        const { localSent, localReceived } = this.counts;

        // get unique stream ids sent and received on
        const streamIds = [
            ...new Set([...localSent.keys(), ...localReceived.keys()]),
        ];

        return {
            items: streamIds.map(_ => {
                return {
                    streamId: _,
                    sentCount: localSent.get(_) ?? 0,
                    receivedCount: localReceived.get(_) ?? 0,
                };
            }),
            timestamp,
        };
    }

    public incrementLocalSent(message: IRtcStreamMessage) {
        const { localSent } = this.counts;
        const streamId = encodeStreamId(message.header.stream);
        localSent.set(streamId, (localSent.get(streamId) || 0) + 1);
    }

    public incrementLocalReceived(message: IRtcStreamMessage) {
        const { localReceived } = this.counts;
        const streamId = encodeStreamId(message.header.stream);

        const previouslyReceived = localReceived.get(streamId) || 0;
        if (previouslyReceived === 0) {
            const { streamName, streamType } = message.header.stream;
            logger.debug("RTC client received first message for stream", {
                streamName,
                streamType,
            });
        }

        localReceived.set(streamId, previouslyReceived + 1);
    }

    public async uploadMetrics() {
        const { sessionId } = this;
        const statsReport = await this.connection.peerConnection.getStats();
        const unpackedStats: { [id: string]: string } = {};
        statsReport.forEach(stat => (unpackedStats[stat.id] = stat));
        logger.debug("rtc-stats", {
            ...unpackedStats,
            sessionId,
        });

        const { localSent, localReceived, remoteSent, remoteReceived } =
            this.counts;

        const meta = [
            ...new Set([...localSent.keys(), ...localReceived.keys()]),
        ].reduce(
            (acc, streamId) => {
                const localSentMessagesCount = localSent.get(streamId);
                const localReceivedMessagesCount = localReceived.get(streamId);
                const remoteSentMessagesCount = remoteSent.get(streamId);
                const remoteReceivedMessagesCount =
                    remoteReceived.get(streamId);
                return {
                    ...acc,
                    [`local-${streamId}-sent`]: localSentMessagesCount,
                    [`local-${streamId}-received`]: localReceivedMessagesCount,
                    [`remote-${streamId}-sent`]: remoteSentMessagesCount,
                    [`remote-${streamId}-received`]:
                        remoteReceivedMessagesCount,
                };
            },
            { sessionId },
        );
        logger.debug("rtc-message-report", {
            deviceId: this.connection.getRemoteDeviceId(),
            ...meta,
        });
    }
}
